import { CircleAlert, CircleCheck, Clock } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonSecondaire } from '../ui/Primitives';
import type { EtatSource } from '../../lib/useVeille';
import { instantLisible } from './format';

/**
 * Sources surveillées par le robot (CDC §6.11) : ce qu'il lit, ce qu'il y
 * suit, et comment s'est passé son dernier passage. Les références suivies se
 * lisent dans la plateforme — numéros RNCP du catalogue, déclaration
 * d'activité des antennes —, pas dans une liste à tenir.
 */
export function ModaleSources({
  ouverte,
  onFermer,
  sources,
}: {
  ouverte: boolean;
  onFermer: () => void;
  sources: EtatSource[];
}): JSX.Element | null {
  return (
    <Modale
      titre="Sources surveillées"
      sousTitre="Jeux ouverts lus chaque matin par le robot, et à la demande"
      ouverte={ouverte}
      onFermer={onFermer}
    >
      <div className="space-y-4 p-4 sm:p-6">
        {sources.map((source) => {
          const passage = source.dernierPassage;
          return (
            <section key={source.source} className="rounded-xl border border-gray-100 p-4">
              <h3 className="text-sm font-semibold text-codialis-navy">{source.libelle}</h3>
              <div className="mt-2 text-[11px] font-bold uppercase tracking-wide text-codialis-gray">
                {source.source === 'FRANCE_COMPETENCES' ? 'Fiches suivies' : 'Déclarations d’activité suivies'}
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {source.references.length === 0 ? (
                  <span className="text-xs text-codialis-gray">
                    {source.source === 'FRANCE_COMPETENCES'
                      ? 'Aucune : ni formation active avec un numéro RNCP, ni fiche saisie.'
                      : 'Aucune : le numéro de déclaration d’activité des antennes n’est pas renseigné.'}
                  </span>
                ) : (
                  source.references.map((reference) => (
                    <span
                      key={reference}
                      className="rounded-lg bg-codialis-sand px-2 py-1 font-mono text-[11px] text-codialis-navy"
                    >
                      {reference}
                    </span>
                  ))
                )}
              </div>
              <div className="mt-3 flex items-start gap-2 text-xs">
                {!passage ? (
                  <>
                    <Clock className="mt-0.5 h-4 w-4 shrink-0 text-codialis-gray" aria-hidden="true" />
                    <span className="text-codialis-gray">Jamais analysée : premier passage demain à 6 h, ou maintenant.</span>
                  </>
                ) : passage.succes === false ? (
                  <>
                    <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                    <span className="min-w-0 text-codialis-navy">
                      Échec le {instantLisible(passage.debuteLe)} : {passage.message}
                      {source.dernierSucces && (
                        <span className="block text-codialis-gray">
                          Dernière lecture réussie le {instantLisible(source.dernierSucces)}.
                        </span>
                      )}
                    </span>
                  </>
                ) : (
                  <>
                    <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                    <span className="min-w-0 break-words text-codialis-navy">
                      {passage.succes === null ? 'En cours depuis' : 'Lue le'} {instantLisible(passage.debuteLe)}
                      {passage.lancePar ? ` à la demande de ${passage.lancePar}` : ' (passage quotidien)'}
                      {passage.message && <span className="block text-codialis-gray">{passage.message}</span>}
                    </span>
                  </>
                )}
              </div>
            </section>
          );
        })}

        <p className="text-xs text-codialis-gray">
          Légifrance, la DREETS et la Région ne publient pas de flux ouvert que le robot puisse lire : ce
          qu’elles annoncent se saisit avec « Saisir une évolution » et suit la même analyse d’impact.
          Les dates des agréments, de la certification qualité et des habilitations, qu’aucune source
          ouverte ne donne, se saisissent comme échéances.
        </p>

        <div className="flex justify-end">
          <BoutonSecondaire onClick={onFermer}>Fermer</BoutonSecondaire>
        </div>
      </div>
    </Modale>
  );
}
